<template>
  <el-form
    ref="stationForm"
    v-loading="isLoading"
    element-loading-text="載入區域與測站資料中..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(255, 255, 255, 0.85)"
    :model="form"
    :validate-on-rule-change="false"
    :hide-required-asterisk="true"
    label-position="top"
    class="area-station-form"
  >
    <!-- 區域選擇 -->
    <el-row>
      <el-col :span="24">
        <el-form-item
          prop="area"
          label="區域"
        >
          <el-select
            v-model="form.area"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="area in areaList"
              :key="area._id"
              :value="area._id"
              :label="area.name"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- 測站過濾工具列 -->
    <div class="search-toolbar flex-row align-center justify-between mb-16">
      <el-input
        v-model="searchInput"
        placeholder="過濾清單（輸入關鍵字搜尋）..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        class="search-input"
        @input="stationFilter"
        @clear="stationFilter"
      >
        <template slot="append" v-if="searchUseReg">
          <el-tag size="mini" type="success" effect="dark">RegExp</el-tag>
        </template>
      </el-input>
      <div class="regex-toggle-wrapper flex-row align-center">
        <span class="regex-label">正則表達式</span>
        <el-tooltip content="支援正則表達式篩選，例如 TK\d+" placement="top">
          <el-switch
            v-model="searchUseReg"
            active-color="#13ce66"
            inactive-color="#dcdfe6"
            @change="stationFilter"
          ></el-switch>
        </el-tooltip>
      </div>
    </div>

    <!-- 測站配置與拖拉排序區域 -->
    <el-form-item
      prop="station"
      class="station-form-item"
    >
      <div slot="label" class="flex-row justify-between align-center label-header">
        <span class="form-item-title">
          <strong>測站配置</strong>
          <span class="hint-text">（可直接拖曳 Tag 調整順序，點擊 ✕ 移除）</span>
        </span>
        <el-tag size="small" type="info">
          已選 {{ form.station.length }} / 未選 {{ candidateCount }}
        </el-tag>
      </div>
      <el-drag-select
        v-model="form.station"
        multiple
        filterable
        allow-create
        default-first-option
        placeholder="點擊下拉選單選取測站，或輸入名稱 Enter 新增"
        class="full-width-drag-select"
      >
        <el-option
          v-for="station in stationList"
          :key="station._id"
          :label="station.name"
          :value="station._id"
        />
      </el-drag-select>
    </el-form-item>

    <!-- 底部操作列與提示 -->
    <div class="form-footer flex-row justify-between align-center">
      <div class="footer-tip">
        <i class="el-icon-info"></i>
        <span>調整順序請點擊「更新排序」；新增或刪減測站請點擊「更新區域」</span>
      </div>
      <div class="button-group flex-row align-center">
        <el-button
          type="warning"
          icon="el-icon-sort"
          size="medium"
          :loading="isLoading"
          :disabled="!form.station.length || isLoading"
          @click="updateSelectSort"
        >
          更新排序
        </el-button>
        <el-button
          type="primary"
          icon="el-icon-check"
          size="medium"
          :loading="isLoading"
          :disabled="!selectedArea || isLoading"
          @click="submitForm"
        >
          更新區域
        </el-button>
      </div>
    </div>
  </el-form>
</template>

<script>
export default {
  props: {
    areaId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isInitialized: false,
      form: {
        area: '',
        station: [],
      },
      areaList: [],
      allStationList: [],
      stationList: [],
      searchInput: '',
      searchUseReg: false,
    };
  },
  computed: {
    selectedArea() {
      const selectedAreaId = this.form.area;
      const selectedArea = this.areaList.find(ele => ele._id === selectedAreaId);
      return selectedArea;
    },
    stationIdList() {
      return this.allStationList.map(station => station._id);
    },
    candidateCount() {
      return this.allStationList.length;
    },
  },
  watch: {
    areaId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.area = newValue;
      }
    },
    'form.area'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.station = [];
        this.setAreaStation();
        if (this.isInitialized && newValue) {
          this.searchInput = '';
          this.stationFilter();
        }
      }
    },
  },
  async mounted() {
    if (this.areaId) {
      this.form.area = this.areaId;
    }
    this.isLoading = true;
    try {
      await Promise.all([
        this.getAreaList(),
        this.getStationList(),
      ]);
      this.setAreaStation();
      this.stationFilter();
    } catch (e) {
      this.$notifyError(e);
    } finally {
      this.isLoading = false;
      this.isInitialized = true;
    }
  },
  methods: {
    setAreaStation() {
      if (this.selectedArea && Array.isArray(this.selectedArea.station)) {
        this.form.station = [...this.selectedArea.station];
      } else {
        this.form.station = [];
      }
    },
    getAreaList(isUpdate = false) {
      return this.$store
        .dispatch('data/getAreaList', isUpdate)
        .then(res => {
          this.areaList = res || [];
          return this.areaList;
        })
        .catch(this.$notifyError);
    },
    getStationList(isUpdate = false) {
      return this.$store
        .dispatch('data/getStationList', isUpdate)
        .then(res => {
          this.allStationList = res || [];
          return this.allStationList;
        })
        .catch(this.$notifyError);
    },
    stationFilter() {
      const currentSelectedIds = new Set(
        (this.form.station || []).map(id => (typeof id === 'object' && id !== null ? id._id : id)),
      );

      let pool = [...this.allStationList];
      const query = this.searchInput ? this.searchInput.trim() : '';
      if (query) {
        if (this.searchUseReg) {
          try {
            const reg = new RegExp(query, 'i');
            pool = pool.filter(ele => reg.test(ele.name) || currentSelectedIds.has(ele._id));
          } catch (e) {
            // regex fallback
          }
        } else {
          const lowerQuery = query.toLowerCase();
          pool = pool.filter(ele => (ele.name && ele.name.toLowerCase().includes(lowerQuery)) || currentSelectedIds.has(ele._id));
        }
      }

      // 保底聯集：確保已選項目都存在於 stationList，避免顯示 ObjectId
      const map = new Map();
      pool.forEach(item => map.set(item._id, item));
      currentSelectedIds.forEach(id => {
        if (!map.has(id)) {
          const found = this.allStationList.find(i => i._id === id);
          if (found) {
            map.set(id, found);
          } else {
            map.set(id, { _id: id, name: id });
          }
        }
      });

      this.stationList = Array.from(map.values());
    },
    async updateSelectSort() {
      let message = '此操作將改變測站排序';
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        let station = this.form.station;
        if (!station.length) {
          this.$notifyError('測站列表不能為空');
          this.isLoading = false;
          return;
        }

        try {
          await this.removeAllStationFromArea();
          await this.addStationToArea(station);

          this.$notifySuccess('修改成功');
          await this.getStationList(true);
          await this.getAreaList(true);
          this.setAreaStation();
          this.stationFilter();
        } catch (error) {
          console.log(error);
          this.$notifyError(error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async removeAllStationFromArea() {
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `area/${this.form.area}/allstation`,
        data: {
          area: [],
        },
      }).catch(this.$notifyError);
    },
    submitForm() {
      this.$refs.stationForm.validate(async valid => {
        if (valid) {
          this.updateAreaStation();
        } else {
          return false;
        }
      });
    },
    async addStationToArea(stationIdList) {
      if (!stationIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: `area/${this.form.area}/station`,
        data: {
          station: stationIdList,
        },
      }).catch(this.$notifyError);
    },
    async removeStationFromArea(stationIdList) {
      if (!stationIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `area/${this.form.area}/station`,
        data: {
          station: stationIdList,
        },
      }).catch(this.$notifyError);
    },
    async createAreaNewStation(stationIdList) {
      if (!stationIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: 'station',
        data: {
          area: this.form.area,
          station: stationIdList,
        },
      }).catch(this.$notifyError);
    },
    async updateAreaStation() {
      const currentAreaStations = (this.selectedArea && this.selectedArea.station) || [];
      const knownIds = new Set(this.allStationList.map(s => s._id));
      const isExisting = id => knownIds.has(id);

      const removedStation = currentAreaStations.filter(station => {
        return !this.form.station.includes(station);
      });
      const newAddedStation = this.form.station.filter(station => {
        return !isExisting(station) && !currentAreaStations.includes(station);
      });
      const pushedStation = this.form.station.filter(station => {
        return !currentAreaStations.includes(station) && isExisting(station);
      });

      if (!newAddedStation.length && !removedStation.length && !pushedStation.length) {
        return this.$notifyInfo('沒有改動');
      }

      let message = '此操作將';
      if (newAddedStation.length) {
        message += `新增 ${newAddedStation.length} 個測站並加至該區域\n`;
      }
      if (removedStation.length) {
        message += `從該區域移除 ${removedStation.length} 個測站\n`;
      }
      if (pushedStation.length) {
        message += `更新 ${pushedStation.length} 個測站至該區域\n`;
      }
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        Promise.all([
          this.createAreaNewStation(newAddedStation),
          this.removeStationFromArea(removedStation),
          this.addStationToArea(pushedStation),
        ])
          .then(async () => {
            this.$notifySuccess('新增成功');
            await this.getStationList(true);
            await this.getAreaList(true);
            this.setAreaStation();
            this.stationFilter();
          })
          .catch(error => {
            console.log(error);
            this.$notifyError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.area-station-form {
  min-width: 620px;
  max-width: 760px;
  min-height: 280px;

  ::v-deep .el-form-item {
    margin-bottom: 18px;
  }

  ::v-deep .el-select,
  ::v-deep .full-width-drag-select {
    width: 100%;
  }

  ::v-deep .drag-select {
    display: block;
    position: relative;
    width: 100%;

    .el-select__tags {
      position: relative !important;
      top: auto !important;
      left: auto !important;
      transform: none !important;
      width: 100% !important;
      max-width: 100% !important;
      min-height: 42px !important;
      padding: 5px 36px 5px 10px !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      z-index: 1;

      > span {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }

    .el-tag {
      margin: 3px 4px 3px 0;
      cursor: grab;
      user-select: none;
      &:active {
        cursor: grabbing;
      }
    }

    .el-select__input {
      margin-left: 6px;
      height: 28px;
      line-height: 28px;
    }

    .el-input {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      pointer-events: none;
      z-index: 0;

      .el-input__inner {
        height: 100% !important;
        min-height: 42px !important;
      }

      .el-input__suffix {
        pointer-events: auto;
        display: flex;
        align-items: center;
        right: 8px;
      }
    }
  }
}

.search-toolbar {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #ebeef5;

  .search-input {
    max-width: 380px;
  }

  .regex-toggle-wrapper {
    display: flex;
    align-items: center;
  }

  .regex-label {
    font-size: 13px;
    color: #606266;
    margin-right: 8px;
  }
}

.label-header {
  width: 100%;
  margin-bottom: 4px;
}

.form-item-title {
  font-size: 14px;
  color: #303133;
}

.hint-text {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 6px;
}

.form-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.footer-tip {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  i {
    margin-right: 5px;
    color: #e6a23c;
    font-size: 14px;
  }
}

.button-group {
  display: flex;
  align-items: center;
  .el-button + .el-button {
    margin-left: 12px;
  }
}
</style>
